import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Route, RouteComponentProps } from 'react-router-dom';
import {
  connectHits,
} from 'react-instantsearch-dom';
import axios from 'axios';

import { isEqual } from 'lodash';
import Select from 'react-select';
import moment from 'moment';
import Form from 'react-bootstrap/Form';
import DateTimePicker from 'react-datetime-picker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faPlay } from '@fortawesome/free-solid-svg-icons';
import {
  PAGES_ADMIN_PREFIX,
  ALGOLIA_ALL_CONTENT_INDEX,
  BLOGS_ADMIN_PREFIX,
} from '../../../utils/constants';
import DateHelper from '../../../utils/DateHelper';
import AdminURLSync from '../AdminURLSync';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import DateFilterDisplay from '../widgets/filters/display-components/DateFilterDisplay';
import StatusFilterDisplay from '../widgets/filters/display-components/StatusFilterDisplay';
import UserFilterDisplay from '../widgets/filters/display-components/UserFilterDisplay';
import routes from '../../../utils/routes';
import EditSVG from './assets/EditSVG';
import { AlgoliaSimpleFacetOptions, ContentInterface, SearchState } from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';

const Hit: React.FC<ContentInterface> = ({
  id,
  title,
  published_at,
  updated_at,
  author,
  content_type,
  route,
  selected,
  toggleSelected,
}) => {
  const [isHidden, setIsHidden] = useState(false);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Would you like to delete this ${content_type}?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${content_type === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/${id}`)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };

  // TODO: currently no relationship between page and author
  // TODO: update `any`
  const values: any = [
    {
      key: 'edit-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={`${content_type === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/${id}/edit`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete ${title}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'title',
      value: (
        <a
          className="m-0 text-dark-btn-txt font-weight-bold"
          href={route}
        >
          {title}
        </a>
      ),
    },
    {
      key: 'author',
      value: <p className="text-table-light-gray m-0">{author}</p>,
    },
    {
      key: 'published',
      value: published_at ? (
        <p className="text-table-light-gray m-0">
          {
            DateHelper.getLocalizedDateTimeFromTimestamp(published_at)
              .format('YYYY/MM/DD [at] h:mm a')
          }
        </p>
      ) : (
        <></>
      ),
    },
    {
      key: 'updated',
      value: (
        <p className="text-table-light-gray m-0">
          {
            DateHelper.getLocalizedDateTimeFromTimestamp(updated_at)
              .format('YYYY/MM/DD [at] h:mm a')
          }
        </p>
      ),
    },
    {
      key: 'edit',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          className="edit widescreen btn btn-admin-button-gray d-flex align-items-center"
          href={`${content_type === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/${id}/edit`}
        >
          <EditSVG />
          <span className="ml-1">EDIT</span>
        </a>
      ),
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete ${title}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={values}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
}> = ({ hits, selected, toggleSelected }) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
    />
  );
};

const CustomHits = connectHits(Hits);

type Props = AlgoliaSimpleFacetOptions & RouteComponentProps;

const AdminDashboardTablePages: React.FC<Props> = ({
  history,
  location,
  match,
  facets,
}): ReactElement => {
  const getContentType = (browserLocation) => {
    const { search } = browserLocation;
    const query = new URLSearchParams(search);
    return query.get('type') || 'page'; // Default to 'page'
  };

  const loadInitialSearchState = (contentType) => {
    const facetFilters = (contentType === 'page' || contentType === 'blog')
      ? [`content_type:${contentType}`]
      : [];
    return {
      configure: {
        numericFilters: [],
        facetFilters,
      },
      query: '',
    };
  };

  const [contentType, setContentType] = useState(getContentType(location));
  const [externalSearchState, setExternalSearchState] = useState<SearchState>(
    loadInitialSearchState(contentType),
  );
  const [authors, setAuthors] = useState<Array<{
    name: string,
    id: number,
    value?: string,
  }>>([]);
  const [bulkActions, setBulkActions] = useState([
    {
      label: 'Delete',
      value: 'delete',
    },
    {
      label: 'Set Published Status',
      value: 'set_published',
    },
    {
      label: 'Set Publication Date',
      value: 'set_published_at',
    },
  ]);

  useEffect(() => {
    if (contentType) {
      axios.get(routes.apiGetPossibleAuthors(contentType))
        .then((res) => {
          if (res.status === 200) {
            setAuthors(res.data.map((author: { name: string, id: number}) => {
              return {
                label: author.name,
                value: author.id,
              };
            }));
            const newBulkActions = [
              ...bulkActions,
              {
                label: 'Set Author',
                value: 'set_author',
              },
            ];
            setBulkActions(newBulkActions);
          }
        })
        .catch((e) => {
          // console.log(e.response);
        });
    }
  }, [contentType]);

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No content has been selected. Please, select one or more pages/blogs to bulk edit.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} pages?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${contentType === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/${bulkSelected.join(',')}`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'set_published':
          setModalData({
            message: `Would you like to update the 'Published Status' of ${bulkSelected.length} pages?`,
            ModalForm: () => {
              return (
                <div>
                  <Select
                    placeholder="Choose a status..."
                    onChange={(e) => {
                      setModalFormState({ status: e?.value });
                    }}
                    options={[
                      {
                        label: 'Published',
                        value: 'published',
                      },
                      {
                        label: 'Unpublished',
                        value: 'unpublished',
                      },
                      {
                        label: 'Draft',
                        value: 'draft',
                      },

                    ]}
                  />
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              return new Promise((res, rej) => {
                axios.patch(
                  `${contentType === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/${bulkSelected.join(',')}`,
                  fields,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'set_author':
          setModalData({
            message: `Would you like to update the 'Author' of ${bulkSelected.length} pages?`,
            ModalForm: () => {
              return (
                <div>
                  <Select
                    placeholder="Choose an author..."
                    onChange={(e) => {
                      if (e && e.value) {
                        setModalFormState({ user_id: e.value });
                      }
                    }}
                    options={authors}
                  />
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              return new Promise((res, rej) => {
                axios.patch(
                  `${contentType === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/${bulkSelected.join(',')}`,
                  fields,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'set_published_at':
          setModalData({
            message: `Would you like to update the 'Publication Date' of ${bulkSelected.length} pages?`,
            ModalForm: () => {
              const [date, setDate] = useState(new Date());
              useEffect(() => {
                setModalFormState({ published_at: moment.utc(date) });
              }, [date]);
              return (
                <div>
                  <Form>
                    <DateTimePicker
                      onChange={(val) => setDate(val)}
                      value={date}
                    />
                  </Form>
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              return new Promise((res, rej) => {
                axios.patch(
                  `${contentType === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/${bulkSelected.join(',')}`,
                  fields,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  useEffect(() => {
    const type = getContentType(location);

    if (type !== contentType) {
      setContentType(type);
    }
  }, [location]);

  useEffect(() => {
    if (contentType === 'page') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['content_type:page'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['content_type:page'],
            },
          });
        }
      }
    } else if (contentType === 'blog') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['content_type:blog'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['content_type:blog'],
            },
          });
        }
      }
    }
  }, [contentType]);

  const tableHeaderRowValues = [
    {
      key: 'title',
      value: 'Title',
      sort: {
        asc: ALGOLIA_ALL_CONTENT_INDEX,
      },
    },
    {
      key: 'author',
      value: 'Author',
    },
    {
      key: 'published',
      value: 'Published',
      sort: {
        desc: `${ALGOLIA_ALL_CONTENT_INDEX}_published_desc`,
      },
    },
    {
      key: 'updated',
      value: 'Updated',
      sort: {
        desc: `${ALGOLIA_ALL_CONTENT_INDEX}_updated_desc`,
      },
    },
  ];

  // TODO: Had to memoize this, because it was being re-rendered constantly; no idea why
  const ActionButton = useCallback(() => (
    <a
      className="mr-2 btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt"
      href={`${contentType === 'page' ? PAGES_ADMIN_PREFIX : BLOGS_ADMIN_PREFIX}/create`}
    >
      <span className="mr-2 action-font text-uppercase">
        {`Create New ${contentType}`}
      </span>
      <FontAwesomeIcon icon={faPlay} size="xs" />
    </a>
  ), [contentType]);

  return (
    <>
      <AdminURLSync
        title={`Content: ${contentType.replaceAll('_', ' ')}`}
        ActionButton={ActionButton}
        numEmptyStartHeaders={2}
        numEmptyEndHeaders={2}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={bulkActions}
        externalQueryParams={[
          {
            key: 'type',
            value: contentType,
          },
        ]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={`${ALGOLIA_ALL_CONTENT_INDEX}_published_desc`}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
          />
        )}
        attributes={[
          {
            type: 'dateRange',
            attribute: 'published_at',
            startLabel: '',
            endLabel: '',
            DisplayComponent: <DateFilterDisplay title="Published:" />,
          },
          {
            type: 'facet',
            attribute: 'status',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <StatusFilterDisplay />,
            placeholder: '',
            simpleOptions: facets?.status || [],
          },
          {
            type: 'facet',
            attribute: 'author',
            isSearchable: true,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <UserFilterDisplay title="Author:" />,
            placeholder: '',
            simpleOptions: facets?.author || [],
          },
        ]}
        dateAttributes={[]}
        tabs={[
          {
            title: 'Pages',
            route: `${PAGES_ADMIN_PREFIX}?type=page`,
            key: 'type=page',
            indexName: ALGOLIA_ALL_CONTENT_INDEX,
            facetFilters: ['content_type:page'],
            numericFilters: [],
          },
          {
            title: 'Blogs',
            route: `${PAGES_ADMIN_PREFIX}?type=blog`,
            key: 'type=blog',
            indexName: ALGOLIA_ALL_CONTENT_INDEX,
            facetFilters: ['content_type:blog'],
            numericFilters: [],
          },
        ]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminDashboardTablePages;

if (document.getElementById('react-admin-dashboard-table-pages')) {
  const element = document.getElementById('react-admin-dashboard-table-pages');

  const facets = element?.dataset.facets && JSON.parse(element?.dataset.facets);

  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={PAGES_ADMIN_PREFIX}
        render={(routeProps) => (
          <AdminDashboardTablePages
            facets={facets}
            {...routeProps}
          />
        )}
      />
    </BrowserRouter>,
    element,
  );
}
